// ─── Firestore Service (Firebase v8 compat) ───────────────────────────────────
import firebase from "firebase/app";
import { db } from "./config";

const timestamp = () => firebase.firestore.FieldValue.serverTimestamp();

// ── Generic helpers ───────────────────────────────────────────────────────────
const colRef = (col)      => db.collection(col);
const docRef = (col, id)  => db.collection(col).doc(id);

const getAll = async (col, order = "createdAt") => {
  const snap = await colRef(col).orderBy(order, "desc").get();
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

const getOne = async (col, id) => {
  const snap = await docRef(col, id).get();
  return snap.exists ? { id: snap.id, ...snap.data() } : null;
};

const create = async (col, data) => {
  const ref = await colRef(col).add({
    ...data,
    createdAt: timestamp(),
    updatedAt: timestamp(),
  });
  return ref.id;
};

const update = async (col, id, data) => {
  await docRef(col, id).update({ ...data, updatedAt: timestamp() });
};

const remove = async (col, id) => {
  await docRef(col, id).delete();
};

export const deleteField = () => firebase.firestore.FieldValue.delete();

export const incrementProductView = (id) =>
  docRef("products", id).update({
    views: firebase.firestore.FieldValue.increment(1),
  });

// ── Products ──────────────────────────────────────────────────────────────────
export const getProducts   = ()         => getAll("products");
export const getProduct    = (id)       => getOne("products", id);
export const createProduct = (data)     => create("products", data);
export const updateProduct = (id, data) => update("products", id, data);
export const deleteProduct = (id)       => remove("products", id);

// ── Collections ───────────────────────────────────────────────────────────────
export const getCollections   = ()         => getAll("collections", "name");
export const createCollection = (data)     => create("collections", data);
export const updateCollection = (id, data) => update("collections", id, data);
export const deleteCollection = (id)       => remove("collections", id);

// ── Brands ────────────────────────────────────────────────────────────────────
export const getBrands   = ()         => getAll("brands", "name");
export const createBrand = (data)     => create("brands", data);
export const updateBrand = (id, data) => update("brands", id, data);
export const deleteBrand = (id)       => remove("brands", id);

// ── Users ─────────────────────────────────────────────────────────────────────
export const getUsers = () => getAll("users", "createdAt");

// ── Delivery Staff ────────────────────────────────────────────────────────────
export const getDeliveryStaff   = ()       => getAll("delivery_staff", "createdAt");
export const addDeliveryStaff   = (data)   => create("delivery_staff", data);
export const removeDeliveryStaff = (id)    => remove("delivery_staff", id);

// ── Admins ────────────────────────────────────────────────────────────────────
export const getAdmins   = ()         => getAll("admins", "createdAt");
export const addAdmin    = (email)    => create("admins", { email });
export const removeAdmin = (id)       => remove("admins", id);

// ── Referrals ─────────────────────────────────────────────────────────────────
export const getReferrals       = ()         => getAll("referrals", "createdAt");
export const createReferral     = (data)     => create("referrals", data);
export const updateReferral     = (id, data) => update("referrals", id, data);
export const deleteReferral     = (id)       => remove("referrals", id);

export const incrementReferralClick = async (code) => {
  const snap = await db.collection("referrals").where("code", "==", code).get();
  if (!snap.empty) {
    snap.docs[0].ref.update({
      totalClicks: firebase.firestore.FieldValue.increment(1),
      updatedAt:   firebase.firestore.FieldValue.serverTimestamp(),
    });
  }
};

export const getOrdersByReferralCode = async (code) => {
  const snap = await db.collection("orders").where("referralCode", "==", code).get();
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
};

export const getReferralByCode = async (code) => {
  const snap = await db.collection("referrals").where("code", "==", code.toUpperCase()).get();
  if (snap.empty) return null;
  return { id: snap.docs[0].id, ...snap.docs[0].data() };
};

export const getReferralByUserId = async (userId) => {
  const snap = await db.collection("referrals").where("userId", "==", userId).get();
  if (snap.empty) return null;
  return { id: snap.docs[0].id, ...snap.docs[0].data() };
};

// ── Store Settings ────────────────────────────────────────────────────────────
const SETTINGS_DEFAULTS = { cardStyle: "standard", listingMode: "collections", productView: "inline", eventsEnabled: false };

export const getStoreSettings = async () => {
  const snap = await db.collection("settings").doc("store").get();
  return snap.exists ? { ...SETTINGS_DEFAULTS, ...snap.data() } : SETTINGS_DEFAULTS;
};

export const updateStoreSettings = (data) =>
  db.collection("settings").doc("store").set(data, { merge: true });

// ── Orders ────────────────────────────────────────────────────────────────────
export const getOrders   = ()         => getAll("orders");
export const updateOrder = (id, data) => update("orders", id, data);

export const ORDER_STATUSES = ["Pending", "Confirmed", "Shipped", "Delivered", "Cancelled"];

export const createOrder = (data) => create("orders", { ...data, status: "Pending" });

export const getUserOrders = async (userId) => {
  const snap = await db.collection("orders")
    .where("userId", "==", userId)
    .get();
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
};

// ── Withdrawals ───────────────────────────────────────────────────────────────
export const getWithdrawals    = ()         => getAll("withdrawals");
export const createWithdrawal  = (data)     => create("withdrawals", data);
export const updateWithdrawal  = (id, data) => update("withdrawals", id, data);

// ── Referral Payout Requests ─────────────────────────────────────────────────
// Manual flow: referral owner requests a payout, admin pays them directly via
// mobile money outside the app, then marks the request Paid.
export const getReferralWithdrawals       = ()         => getAll("referral_withdrawals");
export const createReferralWithdrawal     = (data)     => create("referral_withdrawals", data);
export const updateReferralWithdrawal     = (id, data) => update("referral_withdrawals", id, data);

export const getReferralWithdrawalsByCode = async (code) => {
  const snap = await db.collection("referral_withdrawals").where("referralCode", "==", code).get();
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
};

// ── Events ────────────────────────────────────────────────────────────────────
export const getEvent    = (id)       => getOne("events", id);
export const createEvent = (data)     => create("events", data);
export const updateEvent = (id, data) => update("events", id, data);
export const deleteEvent = (id)       => remove("events", id);

export const getEvents = async () => {
  const snap = await db.collection("events").orderBy("eventDate", "asc").get();
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const getEventsByOwner = async (ownerId) => {
  const snap = await db.collection("events").where("ownerId", "==", ownerId).get();
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

// ── Event Owner Withdrawals ───────────────────────────────────────────────────
// Manual flow, same shape as Referral Payout Requests: an event owner
// requests a payout of their ticket revenue, admin pays them directly via
// mobile money outside the app, then marks the request Paid.
export const getEventWithdrawals   = ()         => getAll("event_withdrawals");
export const createEventWithdrawal = (data)     => create("event_withdrawals", data);
export const updateEventWithdrawal = (id, data) => update("event_withdrawals", id, data);

export const getEventWithdrawalsByOwner = async (ownerId) => {
  const snap = await db.collection("event_withdrawals").where("ownerId", "==", ownerId).get();
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
};

// ── Event Staff ───────────────────────────────────────────────────────────────
export const getEventStaff    = ()     => getAll("event_staff", "createdAt");
export const addEventStaff    = (data) => create("event_staff", data);
export const removeEventStaff = (id)   => remove("event_staff", id);

// ── Tickets ───────────────────────────────────────────────────────────────────
// Written directly by the client after payment confirms — same trust model
// as createOrder. A hardened version (server-verified via a Netlify Function
// before any ticket is written) exists in src/services/ticketing.js's git
// history / netlify/functions/finalize-ticket-purchase.js if needed later.
export const getTickets    = ()         => getAll("tickets");
export const createTicket  = (data)     => create("tickets", data);
export const updateTicket  = (id, data) => update("tickets", id, data);

export const getUserTickets = async (userId) => {
  const snap = await db.collection("tickets").where("userId", "==", userId).get();
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
};
